import { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { usePagination } from '@/lib/v3/components/Table';
import { OrderColumnState } from '@/lib/v3/components/Table/Table';

import {
  ETriggerActivityType,
  GetRulesPayload,
  JourneyMessageTemplatesStatus,
  RulesSortField,
} from '@/src/infrastructure/interfaces/IAutomation.interface';
import { useAutomationService } from '@/src/infrastructure/Protocol/Automation/useAutomationService';
import { CheckboxItems } from '@/src/modules/AutomationModuleNew/components/DropdownFilterSection/DropdownFilterSection';
import { useTranslationsTypesRules } from '@/src/modules/AutomationModuleNew/hooks/useTranslationsTypesRules';
import { RulesTable } from '@/src/modules/AutomationModuleNew/interfaces/automation';

const defaultFilters: GetRulesPayload = {
  search: '',
  status: [],
  page: 1,
  limit: 10,
  creationDateBefore: null,
  creationDateAfter: null,
  sortField: 'creationDate',
  sortOrder: 'desc',
  ruleTypesToFetch: [],
};

export const useRulesFilters = () => {
  const service = useAutomationService();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [countDataList, setCountDataList] = useState<{ total: number; partial: number }>({
    total: 0,
    partial: 0,
  });
  const [hasFiltersApplied, setHasFiltersApplied] = useState(false);

  const { rulesTypesTrans } = useTranslationsTypesRules();

  const defaultDropDownItems = useCallback(
    (statusUrl?: string | null, typesUrl?: string | null): CheckboxItems => {
      const statusUrlSplit = statusUrl?.split(',') as JourneyMessageTemplatesStatus[];
      const typesUrlSplit = typesUrl?.split(',');
      return {
        status: {
          label: t('RecurringMessagesFilters.filter.status'),
          items: [
            {
              id: 'active',
              label: t('useJourneysMessageFilters.filter.active'),
              value: 'active',
              checked: statusUrlSplit?.includes('active') || false,
            },
            {
              id: 'inactive',
              label: t('useJourneysMessageFilters.filter.inactive'),
              value: 'inactive',
              checked: statusUrlSplit?.includes('inactive') || false,
            },
            {
              id: 'draft',
              label: t('useJourneysMessageFilters.filter.draft'),
              value: 'draft',
              checked: statusUrlSplit?.includes('draft') || false,
            },
          ],
        },
        ruleTypesToFetch: {
          label: t('RecurringMessagesFilters.filter.ruleTypesToFetch'),
          items: [
            {
              id: 'trigger_date',
              label: rulesTypesTrans[ETriggerActivityType.TRIGGER_DATE] as string,
              value: ETriggerActivityType.TRIGGER_DATE,
              checked: typesUrlSplit?.includes(String(ETriggerActivityType.TRIGGER_DATE)) || false,
            },
            {
              id: 'external_url_call',
              label: rulesTypesTrans[ETriggerActivityType.EXTERNAL_URL_CALL] as string,
              value: ETriggerActivityType.EXTERNAL_URL_CALL,
              checked:
                typesUrlSplit?.includes(String(ETriggerActivityType.EXTERNAL_URL_CALL)) || false,
            },
            {
              id: 'api_integrator',
              label: rulesTypesTrans[ETriggerActivityType.API_INTEGRATOR] as string,
              value: ETriggerActivityType.API_INTEGRATOR,
              checked:
                typesUrlSplit?.includes(String(ETriggerActivityType.API_INTEGRATOR)) || false,
            },
            {
              id: 'associated_tag',
              label: rulesTypesTrans[ETriggerActivityType.ASSOCIATED_TAG] as string,
              value: ETriggerActivityType.ASSOCIATED_TAG,
              checked:
                typesUrlSplit?.includes(String(ETriggerActivityType.ASSOCIATED_TAG)) || false,
            },
            {
              id: 'form',
              label: rulesTypesTrans[ETriggerActivityType.FORM] as string,
              value: ETriggerActivityType.FORM,
              checked: typesUrlSplit?.includes(String(ETriggerActivityType.FORM)) || false,
            },
          ],
        },
      };
    },
    [rulesTypesTrans, t]
  );

  const [dropDownItems, setDropDownItems] = useState<CheckboxItems>(
    defaultDropDownItems(searchParams.get('status'), searchParams.get('ruleTypesToFetch'))
  );

  const {
    data,
    setData,
    isLoading,
    valuePagination,
    setRowCount,
    rowCount,
    paginationChange,
    orderColumn,
    orderColumnChange,
    rowSelection,
    rowSelectionChange,
  } = usePagination<RulesTable>({
    initialPaginationState: {
      currentPage: searchParams.get('page')
        ? Number(searchParams.get('page'))
        : defaultFilters.page,
      pageSize: searchParams.get('limit')
        ? Number(searchParams.get('limit'))
        : (defaultFilters.limit as number),
    },
    initialOrderColumn: {
      orderByColumn: searchParams.get('sortField') || (defaultFilters.sortField as string),
      orderByDirection:
        (searchParams.get('sortOrder') as 'asc' | 'desc') ||
        (defaultFilters.sortOrder as 'asc' | 'desc'),
    },
  });

  const [filters, setFilters] = useState<GetRulesPayload>({
    search: searchParams.get('search') || '',
    status: searchParams.get('status')?.split(',') as JourneyMessageTemplatesStatus[],
    ruleTypesToFetch: searchParams
      .get('ruleTypesToFetch')
      ?.split(',')
      .map(Number) as ETriggerActivityType[],
    ...(searchParams.get('creationDateBefore') && {
      creationDateBefore: new Date(searchParams.get('creationDateBefore') as string),
    }),
    ...(searchParams.get('creationDateAfter') && {
      creationDateAfter: new Date(searchParams.get('creationDateAfter') as string),
    }),
    page: valuePagination.currentPage,
    limit: valuePagination.pageSize,
    sortField: (searchParams.get('sortField') as RulesSortField) || 'creationDate',
    sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  });

  const getHasFiltersApplied = useCallback((filters: GetRulesPayload) => {
    return (
      filters.search !== '' ||
      (filters.status && filters.status.length > 0) ||
      (filters.ruleTypesToFetch && filters.ruleTypesToFetch.length > 0) ||
      filters.creationDateAfter instanceof Date ||
      filters.creationDateBefore instanceof Date
    );
  }, []);

  const getData = useCallback(
    async (filters: GetRulesPayload) => {
      const response = await service.getRulesList(filters);

      if (response.success) {
        const {
          data: { result, pager },
        } = response;
        const partial = pager.countPartial ?? 0;
        const total = pager.countTotal ?? 0;
        const hasFiltersApplied = getHasFiltersApplied(filters);
        setHasFiltersApplied(hasFiltersApplied);
        setCountDataList({ total, partial });
        setData(result);
        setRowCount(hasFiltersApplied ? partial : total);
      }
    },
    [getHasFiltersApplied, service, setData, setRowCount]
  );

  const handlePaginationChange = (currentPage: number, pageSize: number) => {
    void paginationChange(currentPage, pageSize, async () => {
      const newFilters = { ...filters, page: currentPage, limit: pageSize };
      updateFiltersUrl(newFilters);
      await getData(newFilters);
    });
  };

  const updateFiltersUrl = useCallback(
    (newFilters: GetRulesPayload) => {
      const updatedFilters = { ...filters, ...newFilters };
      setFilters(updatedFilters);

      const mapDates = {
        ...updatedFilters,
        startDate: updatedFilters.creationDateAfter?.toISOString(),
        endDate: updatedFilters.creationDateBefore?.toISOString(),
      };
      const params = new URLSearchParams();
      Object.entries(mapDates).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) params.set(key, value.join(','));
        } else if (value) {
          params.set(key, String(value));
        }
      });

      setSearchParams(params);
    },
    [filters, setSearchParams]
  );

  const updateUrlAndFetchDataWithResetPagination = useCallback(
    async (updateFilters: GetRulesPayload, noResetPageAndLimit: boolean = false) => {
      const newFilters = {
        ...filters,
        ...updateFilters,
        ...(!noResetPageAndLimit && { page: defaultFilters.page }),
        ...(!noResetPageAndLimit && { limit: defaultFilters.limit }),
      };
      updateFiltersUrl(newFilters);
      await paginationChange(newFilters.page as number, newFilters.limit as number, async () => {
        await getData(newFilters);
      });
    },
    [filters, getData, paginationChange, updateFiltersUrl]
  );

  const getInitialData = useCallback(async () => {
    await updateUrlAndFetchDataWithResetPagination(filters);
  }, [filters, updateUrlAndFetchDataWithResetPagination]);

  const resetFilters = useCallback(async () => {
    await updateUrlAndFetchDataWithResetPagination(defaultFilters);
    setDropDownItems(defaultDropDownItems());
  }, [defaultDropDownItems, updateUrlAndFetchDataWithResetPagination]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters((prevState) => ({ ...prevState, search: value }));
  }, []);

  const handleSearchApply = useCallback(
    (value: string) => {
      const newFilters = {
        ...filters,
        search: value,
      };
      void updateUrlAndFetchDataWithResetPagination(newFilters);
    },
    [filters, updateUrlAndFetchDataWithResetPagination]
  );

  const handleStatusFilterApply = useCallback(
    (checkedItems?: CheckboxItems) => {
      const newFilters = {
        ...filters,
        status:
          (checkedItems?.status?.items.map((item) =>
            item.value.toString()
          ) as JourneyMessageTemplatesStatus[]) || [],
        ruleTypesToFetch:
          (checkedItems?.ruleTypesToFetch?.items.map(
            (item) => item.value
          ) as ETriggerActivityType[]) || [],
      };
      void updateUrlAndFetchDataWithResetPagination(newFilters);
    },
    [filters, updateUrlAndFetchDataWithResetPagination]
  );

  const handleStatusFilterChange = useCallback(
    (isChecked: boolean, section: string, index: number) => {
      setDropDownItems((prevState) => {
        const newItems = { ...prevState };
        newItems[section].items[index].checked = isChecked;
        return newItems;
      });
    },
    []
  );

  const handleDateChange = useCallback(
    (date: Date | [Date | null, Date | null] | null) => {
      if (Array.isArray(date)) {
        setFilters((prevState) => ({
          ...prevState,
          creationDateAfter: date[0],
          creationDateBefore: date[1],
        }));
        if (date[1]) {
          void updateUrlAndFetchDataWithResetPagination({
            ...filters,
            creationDateAfter: date[0],
            creationDateBefore: date[1],
          });
        }
      }
    },
    [filters, updateUrlAndFetchDataWithResetPagination]
  );

  const handleOrderColumnChange = useCallback(
    (value: OrderColumnState) => {
      const newFilters = {
        ...filters,
        sortField: value.orderByColumn as RulesSortField,
        sortOrder: value.orderByDirection,
      };

      void orderColumnChange(value, async () => {
        await updateUrlAndFetchDataWithResetPagination(newFilters, true);
      });
    },
    [filters, orderColumnChange, updateUrlAndFetchDataWithResetPagination]
  );

  const totalRowSelected = Object.keys(rowSelection).length || 0;
  const dataSelected = Object.keys(rowSelection).map((index) => data[Number(index)]);

  return {
    dropDownItems,
    data,
    countDataList,
    getInitialData,
    valuePagination,
    rowCount,
    handlePaginationChange,
    isLoading,
    hasFiltersApplied,
    resetFilters,
    filters,
    handleSearchChange,
    handleSearchApply,
    handleStatusFilterApply,
    handleStatusFilterChange,
    handleDateChange,
    orderColumn,
    handleOrderColumnChange,
    rowSelection,
    rowSelectionChange,
    totalRowSelected,
    dataSelected,
  };
};
